import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { PermissionGuard } from 'features/auth/guards/PermissionGuard';
import { RouteGuard } from 'features/auth/guards/RouteGuard';
import { RoleGuard } from 'features/auth/guards/RoleGuard';
import { AbpPolicies } from 'core/constants/AbpPolicies';
import DashboardLayout from 'features/dashboard/DashboardLayout';
import { NotFoundPage } from 'components/layout/Page/GenericStatusPageFactory';
import { AuthenticationRoutes } from './AuthenticationRoutes';
import SubscriptionProcessRoutes from './SubscriptionProcessRoutes';
import { NewActorPage } from 'features/new-actor/components/NewActorPage';
import ContractDetails from 'features/contract-view/components/contract-details/ContractDetails';
import { SimpleDashboardContent } from 'features/dashboard/DashboardContent';
import { DashboardHeader } from 'features/dashboard/dashboard-header/DashboardHeader';
import PersonDetails from 'features/person-view/components/person-details/PersonDetails';
import { GenericLoading } from 'components/ui/Loader';
import { DeveloperGuard } from 'features/auth/guards/DeveloperGuard';
import { ReferentialDevPage } from 'features/referentials/debug/ReferentialDevCache';
import { SubscriptionProcessProvider } from 'features/subscription-process/contexts';
import { ExcludedCoveragePage } from 'components/layout/Page/ExcludedCoveragePage';
import { BackOfficeRoutes } from './BackOfficeRoutes';
import { GroupContractProcessWrapper } from 'back-office/features/group-contract-process/GroupContractProcessWrapper';
import { SubscriberStep } from 'back-office/features/group-contract-process/steps/SubscriberStep';
import { ProductsStep } from 'back-office/features/group-contract-process/steps/ProductsStep';
import { BrokersStep } from 'back-office/features/group-contract-process/steps/BrokersStep';
import { ContractualizationStep } from 'back-office/features/group-contract-process/steps/ContractualizationStep';
import { GroupContractDetails } from 'back-office/features/group-contract-view/GroupContractDetails';
import DocumentsView from 'features/document-view/DocumentsView';
import MncapMedicalValidation from './MncapMedicalValidation';
import MandateSignatureCallback from './MandateSignatureCallback';
import MncapMedicalSuccess from 'components/layout/StatusPage/MncapMedicalSuccess';
import MncapMedicalError from 'components/layout/StatusPage/MncapMedicalError';
import { DepotSecuriseMedicalPage } from 'features/secure-medical-deposit/DepotSecuriseMedicalPage';
import { PlateformeSwitcher } from 'components/_deprecated/Test';
import UserAccount from 'features/auth/UserAccount';
import { TeamPage } from 'features/access/components/TeamPage';
import { PortefeuillePartagePage } from 'features/portefeuille-partage/components/PortefeuillePartagePage';
import RolesCourtiersPage from 'features/compagnie/RolesCourtiersPage';
import MesHabilitationsTunnelsPage from 'features/compagnie/MesHabilitationsTunnelsPage';
import { CapabilityRoute } from 'features/auth/guards/CapabilityRoute';
import { Capability } from 'core/roles';

const Board = lazy(() => import('features/dashboard/Board'));
const ContractView = lazy(() => import('features/contract-view/components/ContractView'));
const QuoteView = lazy(() => import('features/quote-view/components/QuoteView'));
const SimulationView = lazy(() => import('features/simulation-view/components/SimulationView'));
const GroupContractView = lazy(() => import('features/group-contract-view/components/GroupContractView'));
const ClientsView = lazy(() => import('back-office/features/clients-view/ClientsView'));
const CourtiersView = lazy(() => import('back-office/features/courtiers-view/CourtiersView'));
const LeadsView = lazy(() => import('features/leads-view/components/LeadsView'));
const LeadsExternesView = lazy(() => import('features/leads-externes/components/LeadsExternesView'));
const StatistiquesPage = lazy(() => import('features/statistics-view/components/StatisticsView'));
const NoteView = lazy(() => import('features/note-view/components/NoteView'));
const ActivityFeedPage = lazy(() => import('features/activity-feed/components/ActivityFeedPage'));
const EspaceInfoPage = lazy(() => import('features/espace-info/components/EspaceInfoPage'));
const ValidationsPage = lazy(() => import('features/validations/ValidationsPage'));
const ValidationRequestDetails = lazy(() => import('features/validations/detail/ValidationRequestDetails'));

export const AppRoutes = () => {
  return (
    <Suspense fallback={<GenericLoading loading isFullPage />}>
      <Routes>
        <Route path="/emprunteurs/mncap/med/callback" element={<MncapMedicalValidation />} />
        <Route path="/emprunteurs/mncap/med/success" element={<MncapMedicalSuccess />} />
        <Route path="/emprunteurs/mncap/med/error" element={<MncapMedicalError />} />
        <Route path="/signature/mandat/:token" element={<MandateSignatureCallback />} />
        {/* Dépôt sécurisé médical — page publique accédée par le souscripteur
            via un lien à usage unique reçu par email. Pas d'auth requise,
            doit rester en dehors du RouteGuard. */}
        <Route
          path="/emprunteur/depot-securise/:token"
          element={<DepotSecuriseMedicalPage />}
        />
        {/* Routes d'authentification */}
        {AuthenticationRoutes()}

        {/* Routes protégées du dashboard */}
        <Route
          element={
            <RouteGuard requiresAuth>
              <DashboardLayout />
            </RouteGuard>
          }
        >
          {/* Dashboard home */}
          <Route index element={<Navigate to="/bord" replace />} />
          <Route path="bord" element={<Board />} />

          {/* Flux d'activite */}
          <Route path="flux-activite" element={<ActivityFeedPage />} />

          {/* Routes des contrats avec permissions */}
          <Route path="mes-contrats/*" element={
            <RoleGuard>
              <PermissionGuard
                policy={AbpPolicies.CONTRATS_READ}
                fallback="/bord"
              >
                <Routes>
                  <Route index element={<ContractView />} />
                  <Route
                    path="details/:id"
                    element={
                      <PermissionGuard policy={AbpPolicies.CONTRATS_READ}>
                        <ContractDetails />
                      </PermissionGuard>
                    }
                  />
                </Routes>
              </PermissionGuard>
            </RoleGuard>
          } />

          {/* Routes des devis avec permissions */}
          <Route path="mes-devis/*" element={
            <RoleGuard>
              <PermissionGuard
                policy={AbpPolicies.DEVIS_READ}
                fallback="/bord"
              >
                <Routes>
                  <Route index element={<QuoteView />} />
                </Routes>
              </PermissionGuard>
            </RoleGuard>
          } />

          {/* Routes des simulations */}
          <Route
            path="mes-simulations"
            element={
              <RoleGuard>
                <PermissionGuard policy={AbpPolicies.SIMU_READ} fallback="/bord">
                  <SimulationView />
                </PermissionGuard>
              </RoleGuard>
            }
          />

          {/* Routes des clients avec permissions */}
          <Route path="mes-clients/*" element={
            <RoleGuard>
              <PermissionGuard
                policy={AbpPolicies.ACTEURS_ASSURE}
                fallback="/bord"
              >
                <Routes>
                  {/* Liste des clients du courtier connecté (auto-scopée : /clients/liste) */}
                  <Route index element={<ClientsView />} />
                  <Route
                    path=":id"
                    element={
                      <PermissionGuard policy={AbpPolicies.ACTEURS_ASSURE}>
                        <PersonDetails />
                      </PermissionGuard>
                    }
                  />
                </Routes>
              </PermissionGuard>
            </RoleGuard>
          } />

          {/* Rôle Plateforme : courtiers de la plateforme courante (routes non-admin,
              périmètre imposé côté serveur ; facette « plateformes » masquée).
              Même garde que l'entrée de menu (Courtiers.Read) : sans elle,
              redirection propre au lieu d'une liste en erreur 403. */}
          <Route
            path="mes-courtiers"
            element={
              <RoleGuard>
                <PermissionGuard policy={AbpPolicies.COURTIERS_READ} fallback="/bord">
                  <CourtiersView hiddenFacetKeys={['plateformes']} />
                </PermissionGuard>
              </RoleGuard>
            }
          />

          {/* Routes des leads */}
          <Route path="mes-leads" element={<LeadsView />} />

          {/* Leads partenaires (apporteurs externes) — périmètre porté par le serveur */}
          <Route
            path="mes-leads-partenaires"
            element={
              <RoleGuard>
                <PermissionGuard policy={AbpPolicies.LEADS_MANAGE} fallback="/bord">
                  <LeadsExternesView />
                </PermissionGuard>
              </RoleGuard>
            }
          />

          {/* Validations de devis (Portail Gestion, brique Valideur) — liste et
              détail ; RoleGuard applique routePolicy (Gestion uniquement). */}
          <Route
            path="validations"
            element={
              <RoleGuard>
                <PermissionGuard policy={AbpPolicies.VALIDATIONS_READ} fallback="/bord">
                  <ValidationsPage />
                </PermissionGuard>
              </RoleGuard>
            }
          />
          <Route
            path="validations/:demandeId"
            element={
              <RoleGuard>
                <PermissionGuard policy={AbpPolicies.VALIDATIONS_READ} fallback="/bord">
                  <ValidationRequestDetails />
                </PermissionGuard>
              </RoleGuard>
            }
          />

          {/* Nouveau contact - accessible à tous les utilisateurs authentifiés */}
          <Route
            path="new-contact"
            element={
              <RoleGuard>
                <PermissionGuard requiresAuth>
                  <NewActorPage />
                </PermissionGuard>
              </RoleGuard>
            }
          />

          {/* Statistiques */}
          <Route
            path="mes-statistiques"
            element={
              <RoleGuard>
                <PermissionGuard policy={AbpPolicies.DASHBOARD_STATS} fallback="/bord">
                  <StatistiquesPage />
                </PermissionGuard>
              </RoleGuard>
            }
          />

          {/* PROTECTION DE LA ROUTE DE LA GED — Documents.Read (l'ancienne garde
              DASHBOARD_STATS n'est portée par aucune brique du Portail Plateforme,
              alors que la Consultation portefeuille orga accorde bien la lecture GED) */}
          <Route
            path="mes-documents"
            element={
              <RoleGuard>
                <PermissionGuard policy={AbpPolicies.DOCUMENTS_READ} fallback="/bord">
                  <DocumentsView />
                </PermissionGuard>
              </RoleGuard>
            }
          />

          {/* Routes du processus de souscription */}
          <Route path="simulation/*" element={
            <RoleGuard>
              <SubscriptionProcessRoutes />
            </RoleGuard>
          } />

          {/* Liste des contrats collectifs du courtier connecté */}
          <Route
            path="mes-contrats-collectifs"
            element={
              <RoleGuard>
                <PermissionGuard
                  policy={AbpPolicies.CONTRATS_DE_GROUPE_READ}
                  fallback="/bord"
                >
                  <GroupContractView />
                </PermissionGuard>
              </RoleGuard>
            }
          />

          {/* Routes du parcours de contrat collectif (côté courtier) — même garde
              que la liste, sinon la porte reste ouverte par deep-link */}
          <Route path="mes-contrats-collectifs/process/:groupContractId" element={
            <RoleGuard>
              <PermissionGuard
                policy={AbpPolicies.CONTRATS_DE_GROUPE_READ}
                fallback="/bord"
              >
                <GroupContractProcessWrapper />
              </PermissionGuard>
            </RoleGuard>
          }>
            <Route index element={<Navigate to="souscripteur" replace />} />
            <Route path="souscripteur" element={<SubscriberStep />} />
            <Route path="produits" element={<ProductsStep />} />
            <Route path="courtiers" element={<BrokersStep />} />
            <Route path="contractualisation" element={<ContractualizationStep />} />
          </Route>

          {/* Détail d'un contrat collectif actif (même page que le back-office admin).
              Côté back-office le parent GroupContractView pose les marges et le titre ;
              ici on pose l'enveloppe portail (marges + recherche + fil d'Ariane),
              comme le détail d'un contrat individuel. */}
          <Route
            path="mes-contrats-collectifs/:groupContractId"
            element={
              <RoleGuard>
                <PermissionGuard
                  policy={AbpPolicies.CONTRATS_DE_GROUPE_READ}
                  fallback="/bord"
                >
                  <SimpleDashboardContent>
                    <DashboardHeader />
                    <GroupContractDetails brokerScope />
                  </SimpleDashboardContent>
                </PermissionGuard>
              </RoleGuard>
            }
          />

          {/* Routes des notes — accès piloté par la matrice centrale (Plateforme exclue) */}
          <Route
            path="mes-notes"
            element={
              <CapabilityRoute capability={Capability.NotesAccess}>
                <NoteView />
              </CapabilityRoute>
            }
          />

          {/* Espace Info - Fiches produits et actualites */}
          <Route
            path="espace-info"
            element={
              <EspaceInfoPage />
            }
          />

          {/* Routes du compte courant */}
          <Route
            path="mon-compte"
            element={
              <UserAccount />
            }
          />

          {/* Mon équipe — gestion des collaborateurs du cabinet (Owner only côté UI) */}
          <Route
            path="mon-equipe"
            element={
              <TeamPage />
            }
          />

          {/* Portefeuille partagé — gestion par la PM principale d'une plateforme */}
          <Route
            path="mon-portefeuille-partage"
            element={
              <PortefeuillePartagePage />
            }
          />

          {/* Espace compagnie — PM principale de son plateforme.
              (Le doublon mes-courtiers → MesCourtiersPage a été retiré : masqué
              par la route mes-courtiers déclarée plus haut, il était mort.)
              RoleGuard requis : c'est lui qui applique routePolicy — sans lui,
              la règle Conception d'offre des habilitations serait contournable
              par URL directe. */}
          <Route path="roles-courtiers" element={
            <RoleGuard>
              <RolesCourtiersPage />
            </RoleGuard>
          } />
          <Route path="mes-habilitations-tunnels" element={
            <RoleGuard>
              <MesHabilitationsTunnelsPage />
            </RoleGuard>
          } />

          <Route path="excluded-coverage" element={<ExcludedCoveragePage />} />
          {/* Routes legacy */}
          {/* {OldRoutes()} */}

          {/* 🔧 ROUTE DÉVELOPPEUR - Gestion des référentiels */}
          <Route
            path="dev-referentiels"
            element={
              <DeveloperGuard fallback="/bord">
                <RouteGuard requiresAuth>
                  <ReferentialDevPage />
                </RouteGuard>
              </DeveloperGuard>
            }
          />
        </Route>

        {BackOfficeRoutes()}
        {/* 404 fallback */}

        <Route path="/test" element={<PlateformeSwitcher />} />
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};
